import {
  Switch as HeroUISwitch,
  SwitchProps as HeroUISwitchProps,
  extendVariants,
} from '@heroui/react';
import { ForwardRefExoticComponent, RefAttributes, forwardRef } from 'react';

export type SwitchVariant = 'ios' | 'android';

export interface SwitchProps extends Omit<HeroUISwitchProps, 'color' | 'variant'> {
  label?: string | null;
  variant?: SwitchVariant;
  testID?: string;
}

const SwitchComponent = extendVariants(HeroUISwitch, {
  variants: {
    variant: {
      ios: {
        base: '',
        wrapper: 'm-0 p-1 group-data-[selected=true]:bg-coast-green',
        label: 'pl-3',
        thumb: 'w-6 h-6 shrink-0 border-0 translate-x-[-2px]',
        startContent: '',
        endContent: '',
        thumbIcon: '',
      },
      android: {
        base: '',
        wrapper: [
          'm-0 p-0 w-9 h-3 overflow-visible bg-gray-10',
          'border-[0.5px] border-black/5',
          'group-data-[selected=true]:bg-coast-green-10',
          'group-data-[selected=true]:border-coast-green-90/10',
          'group-data-[focus-visible=true]:ring-0',
          'transition-colors duration-250',
        ],
        label: 'pl-3',
        thumb: [
          'w-5 h-5 shrink-0 shadow-md translate-x-[-5px]',
          'group-data-[selected=true]:translate-x-[0px]',
          'transition-all duration-100',
          'bg-coast-green-10',
          'border-[0.5px] border-black/10',
          'group-data-[disabled=true]:bg-gray-10',
          'group-data-[disabled=true]:shadow-none',
          'group-data-[selected=true]:bg-coast-green',
          'group-data-[selected=true]:border-black/30',
          'group-data-[focus-visible=true]:ring-2',
          'group-data-[focus-visible=true]:ring-offset-2',
          'group-data-[focus-visible=true]:ring-focus',
        ],
        startContent: '',
        endContent: '',
        thumbIcon: '',
      },
    },
  },
  defaultVariants: {
    variant: 'android',
  },
}) as ForwardRefExoticComponent<Omit<SwitchProps, 'ref'> & RefAttributes<HTMLInputElement | null>>;

export const Switch = forwardRef<HTMLInputElement | null, SwitchProps>(
  ({ variant = 'android', label, testID, ...props }, ref) => (
    <SwitchComponent
      ref={ref}
      variant={variant}
      aria-label={label ?? undefined}
      label={label ?? undefined}
      data-testid={testID}
      {...props}
    />
  ),
);

Switch.displayName = 'Switch';
